/* global debounce */
if (!customElements.get('cart-note')) {
  class CartNote extends HTMLElement {
    constructor() {
      super();
      this.disclosure = this.closest('details');

      if (this.disclosure && this.disclosure.matches('.cart-note-disclosure')) {
        this.cartNoteToggle = this.disclosure.querySelector('.js-show-note');
      }

      this.textarea = this.querySelector('textarea');

      // Holds the in-flight save so a newer edit can cancel it. Without this an
      // older, slower response could resolve after a newer one, leaving the UI
      // reporting a stale result for a note the shopper has already changed.
      this.controller = null;
      // Auto-hides the transient "saved" confirmation.
      this.clearStatusTimer = null;

      this.init();
    }

    init() {
      this.status = this.buildStatus();
      this.debouncedHandleNoteChange = debounce(this.handleNoteChange.bind(this), 300);
      // Save as soon as possible, avoid link-click not providing time to save
      this.addEventListener('input', this.debouncedHandleNoteChange);
    }

    /**
     * Creates (once) a polite live region so the save state is announced to
     * assistive tech and shown to sighted shoppers.
     * @returns {HTMLElement}
     */
    buildStatus() {
      let el = this.querySelector('.cart-note__status');
      if (!el) {
        el = document.createElement('span');
        el.className = 'cart-note__status';
        el.setAttribute('role', 'status');
        el.setAttribute('aria-live', 'polite');
        el.setAttribute('aria-atomic', 'true');
        el.hidden = true;
        el.style.cssText = 'display:block;margin-top:0.4em;font-size:0.8125rem;line-height:1.3;';
        (this.textarea || this).insertAdjacentElement('afterend', el);
      }
      return el;
    }

    /**
     * Shows a status message (empty text hides the region).
     * @param {string} text - Message to display.
     * @param {boolean} [isError] - Whether to style it as an error.
     */
    setStatus(text, isError = false) {
      if (!this.status) return;
      clearTimeout(this.clearStatusTimer);
      this.status.textContent = text || '';
      this.status.hidden = !text;
      this.status.style.color = isError ? 'var(--error-text-color, #B8003A)' : '';
    }

    /**
     * Persists the note to the cart, cancelling any earlier in-flight save.
     * @param {Event} evt - Input event from the textarea.
     */
    async handleNoteChange(evt) {
      const { value } = evt.target;

      if (this.cartNoteToggle) {
        const label = value ? theme.strings.editCartNote : theme.strings.addCartNote;
        if (this.cartNoteToggle.textContent !== label) this.cartNoteToggle.textContent = label;
      }

      // Cancel any save still in flight so responses can't arrive out of order.
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.setStatus(CartNote.strings.saving);

      try {
        const response = await fetch(theme.routes.cartUpdate, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ note: value }),
          signal
        });

        if (!response.ok) throw new Error(`Cart update failed with status ${response.status}`);

        // A newer edit may have superseded this request between the await points.
        if (signal.aborted) return;
        this.setStatus(CartNote.strings.saved);
        this.clearStatusTimer = setTimeout(() => this.setStatus(''), 2000);
      } catch (error) {
        // A cancelled request is expected while the shopper keeps typing.
        if (error.name === 'AbortError') return;
        this.setStatus(CartNote.strings.error, true);
      }
    }
  }

  // User-facing status copy. Epoch Shop runs a single (English) locale; if the
  // store adds languages, move these into locale files and theme.strings.
  CartNote.strings = {
    saving: 'Saving note…',
    saved: 'Note saved',
    error: 'Note not saved. Please try again.'
  };

  customElements.define('cart-note', CartNote);
}
